import { Assistant } from "@inrev/assistant";
import { type AssistantConversationId, suretyAccountId, toISODateTime } from "@inrev/common";
import { Option } from "@inrev/core";
import { type InfiniteData, type QueryClient, useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useCallback, useState } from "react";
import { useRequest } from "../../../utils/request";
import { assistantConversationQueryKey, assistantConversationsQueryKey } from "../assistant.keys";
import { parseAssistantStream } from "../parseAssistantStream";

const isAbortError = (error: unknown): boolean =>
	(error instanceof DOMException && error.name === "AbortError") ||
	(error instanceof Error && error.name === "AbortError");

type ExistingAssistantConversation = {
	id: AssistantConversationId;
};

type NewAssistantConversation = {
	create: Assistant.Dtos.Conversation.Create.Request;
};

const isExistingConversation = (
	conversation: ExistingAssistantConversation | NewAssistantConversation,
): conversation is ExistingAssistantConversation => "id" in conversation;

type AssistantHistory = InfiniteData<Assistant.Dtos.Conversation.List.Response, Option<string>>;

/**
 * Names a conversation with nothing saved by the question just sent, as the server will once the
 * turn saves it. Until then the server lists it unnamed, so history reads this name instead.
 */
const nameBySentQuestion = (
	queryClient: QueryClient,
	conversationId: AssistantConversationId,
	message: string,
) => {
	const conversation = queryClient.getQueryData<Assistant.Dtos.Conversation.Get.Response | null>(
		assistantConversationQueryKey(conversationId),
	);
	if (Option.isNone(conversation) || conversation.messages.length > 0) return;
	queryClient.setQueryData(assistantConversationQueryKey(conversationId), {
		...conversation,
		fallbackTitle: Assistant.Text.conversationFallbackTitle(message),
	});
};

/**
 * Moves the conversation to the top of its account's cached history the moment its question is
 * sent. The refresh after the turn replaces it.
 */
const showSentInHistory = (queryClient: QueryClient, conversationId: AssistantConversationId) => {
	const conversation = queryClient.getQueryData<Assistant.Dtos.Conversation.Get.Response | null>(
		assistantConversationQueryKey(conversationId),
	);
	if (Option.isNone(conversation) || conversation.contextType !== "account") return;
	const accountId = suretyAccountId.safeParse(conversation.contextId);
	if (!accountId.success) return;

	queryClient.setQueryData<AssistantHistory>(
		assistantConversationsQueryKey(accountId.data),
		(history) => {
			const [firstPage, ...laterPages] = history?.pages ?? [];
			if (Option.isNone(history) || Option.isNone(firstPage)) return history;
			const listed = history.pages
				.flatMap((page) => page.items)
				.find((item) => item.id === conversationId);
			const sent: Assistant.Dtos.Conversation.List.Item = {
				id: conversationId,
				title: conversation.title,
				fallbackTitle: conversation.fallbackTitle,
				preview: null,
				...listed,
				lastActivityAt: toISODateTime(DateTime.now()),
			};
			const without = (page: Assistant.Dtos.Conversation.List.Response) => ({
				...page,
				items: page.items.filter((item) => item.id !== conversationId),
			});
			return {
				...history,
				pages: [
					{ ...firstPage, items: [sent, ...without(firstPage).items] },
					...laterPages.map(without),
				],
			};
		},
	);
};

export type RunAssistantTurnInput = {
	/** An existing conversation, or the context to create one in before the turn runs. */
	conversation: ExistingAssistantConversation | NewAssistantConversation;
	message: string;
	onEvent: (event: Assistant.Dtos.Turn.Event) => void;
	/** Called once a conversation created for this turn exists, before the turn starts. */
	onCreated?: (conversationId: AssistantConversationId) => void;
	/** Aborts this turn alone, including the create before it. Other turns keep running. */
	signal: AbortSignal;
};

export const useRunAssistantTurn = () => {
	const queryClient = useQueryClient();
	const { post } = useRequest();
	// Each running turn's conversation, from once it exists. A send still creating its own is its
	// caller's to track, since only the caller knows whether it still owns that send.
	const [runningTurns, setRunningTurns] = useState<ReadonlyMap<symbol, AssistantConversationId>>(
		new Map(),
	);

	const runAssistantTurn = useCallback(
		async ({ conversation, message, onEvent, onCreated, signal }: RunAssistantTurnInput) => {
			const turn = Symbol("assistant-turn");
			const trackTurn = (conversationId: AssistantConversationId) =>
				setRunningTurns((current) => new Map(current).set(turn, conversationId));

			const createConversation = async (
				request: Assistant.Dtos.Conversation.Create.Request,
			): Promise<AssistantConversationId> => {
				const created = await post<Assistant.Dtos.Conversation.Get.Response>(
					"/v2/admin/assistant/conversations",
					request,
					"json",
					signal,
				);
				signal.throwIfAborted();
				// Seeded before the caller switches to it, so the switch doesn't refetch.
				queryClient.setQueryData(assistantConversationQueryKey(created.id), created);
				trackTurn(created.id);
				onCreated?.(created.id);
				return created.id;
			};

			if (isExistingConversation(conversation)) trackTurn(conversation.id);
			try {
				const conversationId = isExistingConversation(conversation)
					? conversation.id
					: await createConversation(conversation.create);
				nameBySentQuestion(queryClient, conversationId, message);
				showSentInHistory(queryClient, conversationId);
				const response = await post<Response>(
					`/v2/admin/assistant/conversations/${conversationId}/turns`,
					{
						message,
						timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
					},
					"response",
					signal,
				);
				await parseAssistantStream(response, onEvent, signal);
				// Refetched even while another conversation is open, so the turn hands off to its saved
				// messages rather than a cache that lacks them.
				await queryClient.invalidateQueries({
					queryKey: assistantConversationQueryKey(conversationId),
					refetchType: "all",
				});
			} catch (error) {
				if (isAbortError(error) || signal.aborted) return;
				throw error;
			} finally {
				setRunningTurns((current) => {
					const next = new Map(current);
					next.delete(turn);
					return next;
				});
				// Settles history: the answer's time and preview, or a failed first send dropped.
				void queryClient.invalidateQueries({ queryKey: assistantConversationsQueryKey() });
			}
		},
		[post, queryClient],
	);

	/** Whether a turn is running in the conversation. A blank conversation (`undefined`) has none. */
	const isAssistantTurnStreaming = useCallback(
		(conversationId: AssistantConversationId | undefined) =>
			Option.isSome(conversationId) && [...runningTurns.values()].includes(conversationId),
		[runningTurns],
	);

	return { runAssistantTurn, isAssistantTurnStreaming };
};
